/**
 * Design 3: "Side-by-Side Diff"
 * The disagreement is the focal point. Two reviewer columns dominate the top
 * with their decisions prominently displayed. Citation content below as reference.
 */
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  ExternalLink,
  HelpCircle,
  Loader2,
  User,
  X,
} from "lucide-react";
import { Suspense, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useProjectContext } from "~/components/layout/project-context";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogPortal,
  DialogTitle,
} from "~/components/ui/dialog";
import { Kbd } from "~/components/ui/kbd";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import { $submitAdjudication } from "~/lib/screening/adjudication";
import type { ConflictDetails } from "~/lib/screening/adjudication";
import {
  adjudicationQueueQueryOptions,
  conflictDetailsQueryOptions,
  exclusionCodesQueryOptions,
} from "~/lib/screening/queries";

const searchSchema = z.object({
  citationId: z.string().optional(),
});

export const Route = createFileRoute(
  "/_auth/projects/$projectId/screening/title-abstract/conflicts-v3",
)({
  validateSearch: searchSchema,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(adjudicationQueueQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(exclusionCodesQueryOptions(params.projectId)),
    ]);
  },
  component: ConflictsDesign3,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatAuthors(authors: ConflictDetails["citation"]["authors"]): string {
  if (!authors || authors.length === 0) return "Unknown authors";
  const fmt = (a: { family: string; given?: string }) => {
    if (!a.given) return a.family;
    const initials = a.given.split(" ").map((p) => p.charAt(0)).join("");
    return `${a.family} ${initials}`;
  };
  if (authors.length <= 3) return authors.map(fmt).join(", ");
  return `${authors.slice(0, 3).map(fmt).join(", ")} et al.`;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function DecisionColumn({
  decision,
  label,
}: {
  decision: ConflictDetails["decision1"];
  label: string;
}) {
  const borderColor: Record<string, string> = {
    include: "border-t-emerald-500",
    exclude: "border-t-red-500",
    maybe: "border-t-amber-500",
  };
  const iconBg: Record<string, string> = {
    include: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    exclude: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    maybe: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  };
  const icons: Record<string, React.ReactNode> = {
    include: <Check className="h-4 w-4" />,
    exclude: <X className="h-4 w-4" />,
    maybe: <HelpCircle className="h-4 w-4" />,
  };
  const decisionLabel: Record<string, string> = {
    include: "Include",
    exclude: "Exclude",
    maybe: "Maybe",
  };

  return (
    <div className={`flex-1 rounded-lg border border-t-[3px] ${borderColor[decision.decision] ?? "border-t-gray-400"} bg-white/50 p-4 dark:bg-white/[0.02]`}>
      <div className="mb-3 flex items-center gap-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconBg[decision.decision] ?? "bg-gray-100 text-gray-700"}`}>
          {icons[decision.decision] ?? <User className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{decision.reviewerName}</p>
          <p className="text-muted-foreground text-[11px]">{label}</p>
        </div>
      </div>

      <p className="mb-2 text-lg font-bold capitalize tracking-tight">
        {decisionLabel[decision.decision] ?? decision.decision}
      </p>

      {decision.exclusionCode && (
        <div className="mb-2">
          <span className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs">{decision.exclusionCode}</span>
        </div>
      )}

      {(decision.exclusionNotes || decision.notes) && (
        <div className="mt-3 border-t pt-3">
          <p className="text-muted-foreground mb-1 text-[10px] font-medium uppercase tracking-wider">Notes</p>
          <p className="text-sm leading-relaxed">{decision.exclusionNotes || decision.notes}</p>
        </div>
      )}

      <p className="text-muted-foreground mt-3 text-[11px]">
        {new Date(decision.createdAt).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
function ConflictsDesign3() {
  const { projectId, projectTitle } = useProjectContext();
  const { citationId: searchCitationId } = Route.useSearch();
  const navigate = useNavigate();

  const { data: queue } = useSuspenseQuery(adjudicationQueueQueryOptions(projectId));

  const currentCitationId = useMemo(() => {
    if (searchCitationId && queue.some((c) => c.id === searchCitationId)) return searchCitationId;
    return queue[0]?.id;
  }, [queue, searchCitationId]);

  const goToNext = useCallback(() => {
    navigate({
      to: "/projects/$projectId/screening/title-abstract/conflicts-v3",
      params: { projectId },
      search: {},
      replace: true,
    });
  }, [navigate, projectId]);

  if (queue.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex flex-1 items-center justify-center p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/30">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="mb-2 text-lg font-semibold">No Conflicts to Resolve</h2>
            <p className="text-muted-foreground mb-6 text-sm">All screening conflicts have been resolved.</p>
            <Button render={<Link to="/projects/$projectId" params={{ projectId }} />}>
              Return to Project
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 border-b px-6 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <span className="text-foreground text-sm tabular-nums">
            <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5 text-amber-500" />
            {queue.length} conflict{queue.length === 1 ? "" : "s"} remaining
          </span>
        </div>
      </div>

      {currentCitationId && (
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          }
        >
          <AdjudicationContent
            projectId={projectId}
            citationId={currentCitationId}
            onResolved={goToNext}
          />
        </Suspense>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adjudication content — diff-style
// ---------------------------------------------------------------------------
function AdjudicationContent({
  projectId,
  citationId,
  onResolved,
}: {
  projectId: string;
  citationId: string;
  onResolved: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: conflict } = useSuspenseQuery(conflictDetailsQueryOptions(projectId, citationId));
  const { data: codes } = useSuspenseQuery(exclusionCodesQueryOptions(projectId));

  const [resolution, setResolution] = useState<"include" | "exclude" | null>(null);
  const [rationale, setRationale] = useState("");
  const [exclusionCode, setExclusionCode] = useState("");
  const [showExclusionDialog, setShowExclusionDialog] = useState(false);

  const adjudicateMutation = useMutation({
    mutationFn: (data: {
      resolution: "include" | "exclude";
      rationale: string;
      exclusionCode?: string;
    }) =>
      $submitAdjudication({
        data: {
          projectId,
          citationId,
          resolution: data.resolution,
          rationale: data.rationale,
          exclusionCode: data.exclusionCode,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "adjudication"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "screening"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "dual-screening"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "statistics"] });
      toast.success("Adjudication submitted");
      setResolution(null);
      setRationale("");
      setExclusionCode("");
      onResolved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleResolve = (sel: "include" | "exclude") => {
    if (sel === "exclude") {
      setResolution("exclude");
      setShowExclusionDialog(true);
    } else {
      setResolution(sel);
    }
  };

  const handleSubmit = () => {
    if (!resolution || !rationale.trim()) return;
    adjudicateMutation.mutate({
      resolution,
      rationale: rationale.trim(),
      exclusionCode: resolution === "exclude" ? exclusionCode : undefined,
    });
  };

  const handleExclusionSubmit = (code: string) => {
    setExclusionCode(code);
    setShowExclusionDialog(false);
  };

  const activeCodes = codes.filter((c) => c.isActive);
  const isPending = adjudicateMutation.isPending;
  const { citation, decision1, decision2 } = conflict;

  if (!conflict.canAdjudicate) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-900 dark:bg-amber-950/30">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
          <p className="font-medium text-amber-800 dark:text-amber-200">Cannot adjudicate this conflict</p>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">{conflict.adjudicationReason}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="min-h-0 flex-1 overflow-hidden">
        <main className="px-6 py-8">
          <div className="mx-auto max-w-3xl animate-fade-in-up">
            {/* Side-by-side decision comparison */}
            <div className="mb-8 grid gap-4 md:grid-cols-2">
              <DecisionColumn decision={decision1} label="Reviewer 1" />
              <DecisionColumn decision={decision2} label="Reviewer 2" />
            </div>

            {/* Separator */}
            <div className="relative mb-8">
              <div className="border-border border-t" />
              <span className="bg-background text-muted-foreground absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 px-3 text-[10px] font-medium uppercase tracking-widest">
                Citation
              </span>
            </div>

            {/* Citation */}
            <section className="mb-6">
              <h1 className="font-serif text-2xl font-medium leading-tight">
                {citation.title}
              </h1>
              <p className="text-muted-foreground mt-2 text-sm">
                {formatAuthors(citation.authors)}
                {citation.year != null && ` (${citation.year})`}
              </p>
              {citation.containerTitle && (
                <p className="text-muted-foreground mt-0.5 text-sm italic">
                  {citation.containerTitle}
                  {citation.volume && `, ${citation.volume}`}
                  {citation.issue && `(${citation.issue})`}
                  {citation.pages && `, pp. ${citation.pages}`}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-3">
                {citation.doi && (
                  <a
                    href={`https://doi.org/${citation.doi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                  >
                    DOI <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {citation.pmid && (
                  <a
                    href={`https://pubmed.ncbi.nlm.nih.gov/${citation.pmid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                  >
                    PubMed <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </section>

            {/* Abstract */}
            <section>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Abstract
                </span>
                {citation.abstract && (
                  <span className="text-muted-foreground text-xs">
                    {countWords(citation.abstract)} words
                  </span>
                )}
              </div>
              {citation.abstract ? (
                <p className="text-[15px] leading-relaxed">{citation.abstract}</p>
              ) : (
                <p className="text-muted-foreground text-sm italic">No abstract available</p>
              )}
            </section>
          </div>
        </main>
      </ScrollArea>

      {/* Bottom action bar */}
      {!resolution ? (
        <div className="bg-background shrink-0 border-t p-4">
          <div className="mx-auto flex max-w-2xl items-center justify-center gap-3">
            <Button
              variant="destructive"
              className="text-background"
              onClick={() => handleResolve("exclude")}
              disabled={isPending}
            >
              Exclude
              <Kbd className="ml-2">N</Kbd>
            </Button>
            <Button
              className="bg-[#2B9A66] hover:bg-[#218358]"
              onClick={() => handleResolve("include")}
              disabled={isPending}
            >
              Include
              <Kbd className="ml-2">Y</Kbd>
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-background shrink-0 border-t p-4">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <Label htmlFor="rationale" className="text-sm font-medium">
                  Rationale for{" "}
                  <span className={resolution === "include" ? "text-emerald-600" : "text-red-600"}>
                    {resolution === "include" ? "Include" : "Exclude"}
                  </span>
                  {resolution === "exclude" && exclusionCode && (
                    <span className="text-muted-foreground font-normal"> ({exclusionCode})</span>
                  )}
                </Label>
                <textarea
                  id="rationale"
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Explain your decision..."
                  rows={2}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring mt-2 flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-2 pt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setResolution(null); setRationale(""); setExclusionCode(""); }}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!rationale.trim() || isPending}
                >
                  {isPending ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exclusion code dialog */}
      <Dialog open={showExclusionDialog} onOpenChange={setShowExclusionDialog}>
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup>
            <DialogHeader>
              <DialogTitle>Select Exclusion Reason</DialogTitle>
              <DialogDescription>Choose the reason for excluding this citation.</DialogDescription>
            </DialogHeader>
            <div className="mt-4 max-h-64 space-y-2 overflow-y-auto pr-1">
              {activeCodes.map((code) => (
                <div
                  key={code.id}
                  onClick={() => handleExclusionSubmit(code.code)}
                  className="hover:bg-muted/50 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{code.label}</div>
                    {code.description && (
                      <div className="text-muted-foreground mt-0.5 text-xs">{code.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="mt-4">
              <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
            </DialogFooter>
          </DialogPopup>
        </DialogPortal>
      </Dialog>
    </>
  );
}
